/** Catalogue items and coupons (`plans:*`). Shapes copied from workers/api/src/catalogue (itemView, couponView). Money is integer paise; rates are basis points. */
import type { ApiClient } from "./client";

export const ITEM_KINDS = ["membership", "pt_pack", "class_pack", "service", "product", "fee", "combo"] as const;
export type ItemKind = (typeof ITEM_KINDS)[number];
export type ScopeMode = "one" | "chosen" | "all";

export interface ScopeFields { scopeMode: ScopeMode; brandId: string | null; homeBranchId: string | null; branchIds: string[] }
export interface CatalogueItem extends ScopeFields {
  id: string; kind: ItemKind; name: string; description: string | null; sku: string | null; nonHomeVisitLimit: number | null;
  price: number; priceIncludesTax: boolean; taxRateBp: number; hsnSac: string | null; durationDays: number | null; sessionsTotal: number | null;
  includesClasses: boolean; autoAdd: boolean; active: boolean;
  /** PT packs only. null = the business default (settings, PT booking). */
  bookingStyle: "fixed_slot" | "daily" | "flexible" | null;
  components: { componentId: string; name: string | null; kind: ItemKind | null; quantity: number; position: number }[];
  createdAt: number; updatedAt: number; archivedAt: number | null;
}
export interface Coupon extends ScopeFields {
  id: string; code: string; kind: "percent" | "fixed"; value: number; maxDiscount: number | null; minPurchase: number | null;
  validFrom: string | null; validTo: string | null; maxRedemptions: number | null; maxPerMember: number | null; itemKinds: ItemKind[] | null; active: boolean;
  createdAt: number; updatedAt: number; archivedAt: number | null;
}
type ItemPage = { items: CatalogueItem[]; nextCursor: string | null };
type CouponPage = { coupons: Coupon[]; nextCursor: string | null };

export const PAGE_SIZE = 50;
export const listItems = (c: ApiClient, q: { cursor?: string | null; kind?: string; active?: boolean } = {}) =>
  c.get<ItemPage>("/catalogue/items", { limit: PAGE_SIZE, cursor: q.cursor, kind: q.kind, active: q.active });
/** Every item, for a picker: a choice past the first page must still be offered and shown. */
export async function listAllItems(c: ApiClient): Promise<CatalogueItem[]> {
  const items: CatalogueItem[] = [];
  let cursor: string | null = null;
  do {
    const page: ItemPage = await listItems(c, { cursor });
    items.push(...page.items);
    cursor = page.nextCursor;
  } while (cursor);
  return items;
}
export const getItem = (c: ApiClient, id: string) => c.get<{ item: CatalogueItem }>(`/catalogue/items/${id}`).then((r) => r.item);
export const createItem = (c: ApiClient, body: Record<string, unknown>) => c.post<{ item: CatalogueItem }>("/catalogue/items", body).then((r) => r.item);
/** `expectedUpdatedAt` is required by the API: a stale edit is a 409. */
export const patchItem = (c: ApiClient, id: string, body: Record<string, unknown>) => c.patch<{ item: CatalogueItem }>(`/catalogue/items/${id}`, body).then((r) => r.item);
export const deleteItem = (c: ApiClient, id: string) => c.del<{ item?: CatalogueItem; ok?: true }>(`/catalogue/items/${id}`);

export const listCoupons = (c: ApiClient, q: { cursor?: string | null } = {}) => c.get<CouponPage>("/coupons", { limit: PAGE_SIZE, cursor: q.cursor });
export const getCoupon = (c: ApiClient, id: string) => c.get<{ coupon: Coupon }>(`/coupons/${id}`).then((r) => r.coupon);
export const createCoupon = (c: ApiClient, body: Record<string, unknown>) => c.post<{ coupon: Coupon }>("/coupons", body).then((r) => r.coupon);
export const patchCoupon = (c: ApiClient, id: string, body: Record<string, unknown>) => c.patch<{ coupon: Coupon }>(`/coupons/${id}`, body).then((r) => r.coupon);
export const deleteCoupon = (c: ApiClient, id: string) => c.del<{ coupon?: Coupon; ok?: true }>(`/coupons/${id}`);
